import {
    Button,
    ErrorAlert,
    FormError,
    Notice,
    PasswordInput,
    TextInput,
    linkStyles,
    useTextForm,
} from '@lumlab/platform-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { authClient } from '../app/auth.ts';
import { AuthScreen, authErrorCode } from '../app/AuthScreen.tsx';

const notices = ['passwordReset', 'emailVerified'] as const;

export function SignInPage() {
    const { t } = useTranslation();
    const session = authClient.useSession();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as { notice?: string } | null;
    const notice = notices.find((candidate) => candidate === state?.notice);
    const [error, setError] = useState<{ code: string } | null>(null);
    const [pending, setPending] = useState(false);
    const form = useTextForm({ initialValues: { email: '', password: '' } });

    const submit = form.onSubmit(async (values) => {
        setPending(true);
        setError(null);
        const email =
            import.meta.env.DEV && ['user', 'admin'].includes(values.email.trim().toLowerCase())
                ? `${values.email.trim().toLowerCase()}@lumlab.test`
                : values.email.trim();
        try {
            const result = await authClient.signIn.email({ ...values, email });
            // An account still to be confirmed: the server has just sent it a code.
            if (result.error?.code === 'EMAIL_NOT_VERIFIED')
                return void navigate('/verify-email', { state: { email } });
            if (result.data && 'twoFactorRedirect' in result.data && result.data.twoFactorRedirect)
                return void navigate('/two-factor');
            if (result.error) setError({ code: authErrorCode(result.error) });
        } catch {
            setError({ code: 'internal' });
        } finally {
            setPending(false);
        }
        // On success the session store refreshes, and the redirect below happens.
    });

    if (session.data) {
        return <Navigate to="/" replace />;
    }

    return (
        <AuthScreen title={t('platform.auth.title')}>
            <form onSubmit={submit} className="flex flex-col gap-4">
                <FormError message={form.formError} />
                {notice && !error && <Notice tone="success">{t(`platform.auth.notices.${notice}`)}</Notice>}
                {import.meta.env.DEV && <p className="text-sm text-muted">{t('platform.auth.developmentAccess')}</p>}
                {error && <ErrorAlert error={error} />}
                <TextInput
                    type={import.meta.env.DEV ? 'text' : 'email'}
                    autoComplete={import.meta.env.DEV ? 'username' : 'email'}
                    required
                    label={import.meta.env.DEV ? t('platform.auth.identifier') : t('platform.auth.email')}
                    {...form.getInputProps('email')}
                />
                <PasswordInput
                    autoComplete="current-password"
                    required
                    label={t('platform.auth.password')}
                    {...form.getInputProps('password')}
                />
                <Button type="submit" loading={pending}>
                    {t('platform.auth.submit')}
                </Button>
            </form>
            <Link to="/sign-up" className={`text-sm ${linkStyles}`}>
                {t('platform.auth.signUp.link')}
            </Link>
            <Link to="/forgot-password" className={`text-sm ${linkStyles}`}>
                {t('platform.auth.forgotPasswordLink')}
            </Link>
        </AuthScreen>
    );
}
